/**
 * Cloudflare Pages API endpoints for deployment.
 */

import { api, ApiError } from './client';
import type {
  ApiResponse,
  CloudflareAccount,
  CloudflareProject,
  CloudflareDeployment,
  CloudflareDomainStatus,
  VerifyCloudflareTokenRequest,
  CreateCloudflareProjectRequest,
  DeployToCloudflareRequest,
  AddCloudflareDomainRequest,
} from '../types';

const siteQuery = (siteSlug?: string) => siteSlug === undefined ? '' : `?site_slug=${encodeURIComponent(siteSlug)}`;

/** Cloudflare API functions */
/**
 * The server's deploy errors already say what to do. A gateway or network
 * failure carries no envelope and would surface as a bare "HTTP 504", so give
 * those the same what-happened / what-now shape.
 */
export function deployError(error: unknown): unknown {
  if (error instanceof ApiError) {
    if (error.data.code || error.status < 502) return error;
    return new ApiError(error.status, {
      ...error.data,
      error: `The server did not answer in time (HTTP ${error.status}). Your live site is unchanged unless the deployment list shows a new deployment. Wait a minute, check the deployment list, then try again. If it keeps happening, contact support.`,
    });
  }
  if (error instanceof TypeError) {
    return new Error('Could not reach WordPuppi. Check your connection, then check the deployment list before trying again.');
  }
  return error;
}

export const cloudflareApi = {
  /** Verify a Cloudflare API token and get account info */
  async verifyToken(
    data: VerifyCloudflareTokenRequest
  ): Promise<ApiResponse<CloudflareAccount[]>> {
    return api.post('/v1/cloudflare/verify', data);
  },

  /** List Cloudflare Pages projects */
  async listProjects(siteSlug?: string): Promise<ApiResponse<CloudflareProject[]>> {
    return api.get(`/v1/cloudflare/projects${siteQuery(siteSlug)}`);
  },

  /** Create a new Cloudflare Pages project */
  async createProject(
    data: CreateCloudflareProjectRequest
  ): Promise<ApiResponse<CloudflareProject>> {
    return api.post('/v1/cloudflare/projects', data);
  },

  /** Select the site's project using its saved, encrypted credentials. */
  async selectProject(data: { site_slug?: string; project_name: string }): Promise<ApiResponse<{ project_name: string }>> {
    return api.put('/v1/cloudflare/projects/selection', data);
  },

  /** Deploy to Cloudflare Pages */
  async deploy(data?: DeployToCloudflareRequest): Promise<ApiResponse<CloudflareDeployment>> {
    try {
      return await api.post('/v1/cloudflare/deploy', data ?? {});
    } catch (error) {
      throw deployError(error);
    }
  },

  /** List deployment history */
  async listDeployments(siteSlug?: string): Promise<ApiResponse<CloudflareDeployment[]>> {
    return api.get(`/v1/cloudflare/deployments${siteQuery(siteSlug)}`);
  },

  /** Get a specific deployment */
  async getDeployment(deploymentId: string, siteSlug?: string): Promise<ApiResponse<CloudflareDeployment>> {
    return api.get(`/v1/cloudflare/deployments/${encodeURIComponent(deploymentId)}${siteQuery(siteSlug)}`);
  },

  /** Get domain verification status for CF Pages project */
  async getDomainStatus(siteSlug?: string): Promise<ApiResponse<CloudflareDomainStatus[]>> {
    return api.get(`/v1/cloudflare/domains${siteQuery(siteSlug)}`);
  },

  /** Add a custom domain to a CF Pages project */
  async addDomain(data: AddCloudflareDomainRequest): Promise<ApiResponse<{ domain: string; project_name: string; dns_updated: boolean }>> {
    return api.post('/v1/cloudflare/domains', data);
  },
};
